<template>
  <!-- Alert Mode -->
  <template v-if="useAlert">
    <v-alert
      v-if="!isOnline"
      v-model="alertVisible"
      class="mb-4"
      closable
      close-label="Dismiss"
      icon="mdi-wifi-alert"
      type="error"
    >
      <div class="d-flex justify-space-between align-center">
        <div>
          No Internet connection. <b>Some features may be unavailable.</b>
        </div>
      </div>
    </v-alert>

    <v-alert
      v-else-if="shouldShowRestoreAlert"
      v-model="alertVisible"
      class="mb-4"
      closable
      close-label="Dismiss"
      icon="mdi-wifi"
      type="success"
    >
      <div class="d-flex justify-space-between align-center">
        <div>
          Internet connection has been restored.
          <span v-if="offlineDuration > 0" class="ml-2">
            (Offline for {{ formatDuration(offlineDuration) }})
          </span>
        </div>
      </div>
    </v-alert>
  </template>

  <!-- Banner Mode -->
  <template v-else>
    <v-banner
      v-if="!isOnline"
      class="mb-4"
      color="warning"
      icon="mdi-wifi-alert"
    >
      <template #text>
        No Internet connection.
        <b class="text-warning">Some features may be unavailable.</b>
      </template>
    </v-banner>

    <v-banner
      v-else-if="shouldShowRestoreBanner"
      class="mb-4"
      color="success"
      icon="mdi-wifi"
    >
      <template #text>
        Your connection to the internet has been restored.
        <span v-if="offlineDuration > 0" class="ml-2">
          (Offline for {{ formatDuration(offlineDuration) }})
        </span>
      </template>
      <template #actions>
        <v-btn @click="dismissNotification"> Dismiss </v-btn>
      </template>
    </v-banner>
  </template>
</template>

<script setup>
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

  const props = defineProps({
    autoDismiss: {
      default: 0, // Auto dismiss after N milliseconds, 0 = no auto dismiss
      type: Number,
    },
    useAlert: {
      default: false,
      type: Boolean,
    },
  })

  const isOnline = ref(navigator.onLine)
  const alertVisible = ref(true)
  const restoreNotificationVisible = ref(false)
  const wasOffline = ref(false)
  const offlineStart = ref(null)
  const offlineDuration = ref(0)

  // Computed properties for cleaner template logic
  const shouldShowRestoreAlert = computed(() => {
    return (
      props.useAlert && restoreNotificationVisible.value && alertVisible.value
    )
  })

  const shouldShowRestoreBanner = computed(() => {
    return !props.useAlert && restoreNotificationVisible.value
  })

  function dismissNotification() {
    restoreNotificationVisible.value = false
    alertVisible.value = false
    offlineDuration.value = 0
  }

  function formatDuration(ms) {
    if (!ms) return ''
    const sec = Math.floor(ms / 1000) % 60
    const min = Math.floor(ms / 60_000)
    if (min > 0) return `${min}m ${sec}s`
    return `${sec}s`
  }

  function handleOffline() {
    isOnline.value = false
    restoreNotificationVisible.value = false
    wasOffline.value = true
    offlineStart.value = Date.now()
    alertVisible.value = true // Show offline notification
  }

  function handleOnline() {
    isOnline.value = true
    if (wasOffline.value && offlineStart.value) {
      offlineDuration.value = Date.now() - offlineStart.value
      restoreNotificationVisible.value = true
      alertVisible.value = true // Reset alert visibility for new notification
      wasOffline.value = false
      offlineStart.value = null

      // Auto dismiss if specified
      if (props.autoDismiss > 0) {
        setTimeout(() => {
          dismissNotification()
        }, props.autoDismiss)
      }
    }
  }

  onMounted(() => {
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
  })
</script>
